'use client';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { memo } from 'react';

import { usePortalStore } from '@air/primitive-portal';
import { cn } from '@air/utils-tailwind';

export const TooltipProvider = RadixTooltip.Provider;

export type TooltipProps = Pick<
  RadixTooltip.TooltipProps,
  'defaultOpen' | 'delayDuration' | 'disableHoverableContent' | 'onOpenChange' | 'open'
> &
  Pick<RadixTooltip.TooltipTriggerProps, 'children'> &
  Pick<RadixTooltip.TooltipPortalProps, 'container'> &
  Pick<
    RadixTooltip.TooltipContentProps,
    'align' | 'alignOffset' | 'avoidCollisions' | 'className' | 'side' | 'sideOffset'
  > & {
    'data-testid'?: string;
    hideArrow?: boolean;
    label?: RadixTooltip.TooltipContentProps['children'];
  };

export const Tooltip = memo(
  ({
    align,
    alignOffset,
    avoidCollisions,
    children,
    className,
    container,
    'data-testid': testId,
    defaultOpen,
    delayDuration = 500,
    disableHoverableContent = false,
    hideArrow = false,
    label,
    onOpenChange,
    open,
    side,
    sideOffset = 8,
  }: TooltipProps) => {
    const portal = usePortalStore((state) => state.portal);

    if (!label) {
      return <>{children}</>;
    }

    return (
      <RadixTooltip.Root
        defaultOpen={defaultOpen}
        delayDuration={delayDuration}
        disableHoverableContent={disableHoverableContent}
        onOpenChange={onOpenChange}
        open={open}
      >
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
        <RadixTooltip.Portal container={container || portal}>
          <RadixTooltip.Content
            align={align}
            alignOffset={alignOffset}
            avoidCollisions={avoidCollisions}
            className={cn(
              'z-portal min-h-[32px] rounded-4 bg-black px-2 py-1.5 font-medium text-14 text-white',
              className,
            )}
            data-testid={testId}
            side={side}
            sideOffset={sideOffset}
          >
            {label}
            {!hideArrow && <RadixTooltip.Arrow />}
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    );
  },
);

Tooltip.displayName = 'Tooltip';
